import React, { useEffect, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Actions
import {
  fetchAdminUsers,
  fetchAdminUsersFilters,
  fetchUserWalletHistory,
  userBulkActions,
} from '~/redux/actions/admin-users-actions';
import { CLEAR_ADMIN_USERS_FILTERS } from '~/redux/actions/action-types';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { DEFAULT_PAGINATION, DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { debounce, isNumberString } from '~/utils/helperFunctions';

// Components
import Table from './Table';
import AdvanceFilters from './Filters';
import ActionModal from './ActionModal';
import AddFundsModal from './AddFundsModal';
import NoAdminUsersScreen from './NoAdminUsersScreen';
import ExportModal from '~/components/Contacts/ExportModal';
import RemoveFundsByAdmin from './RemoveFunds';
import TransactionsModal from './TransactionsModal';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';

// MUI Components
import { Container, Box, Grid, Typography, useMediaQuery } from '@mui/material';

// Icons
import { ExportModalCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const AdminUsers = () => {
  const { userId } = useParams();
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const [isMounted, setIsMounted] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [transactionPagination, setTransactionPagination] = useState(
    DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE
  );
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedUser, setSelectedUser] = useState(false);
  const [openAdvanceFilters, setOpenAdvanceFilters] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [openFundsModal, setOpenFundsModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [exportModal, setExportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openRemoveFundsModal, setOpenRemoveFundsModal] = useState(false);
  const [openTransactionsModal, setOpenTransactionsModal] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const childRef = useRef();
  const dispatch = useDispatch();

  const applyFilters = useSelector((state) => state.adminUsersReducers.applyFilters);
  const filters = useSelector((state) => state.adminUsersReducers.filters);
  const adminUsers = useSelector((state) => state.adminUsersReducers.totalUsersInDB);
  const filterSize = useSelector((state) => state.adminUsersReducers.filterSize);

  const handleSearch = () => {
    if (search) {
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getAdminUsers();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const debouncedHandleKeyDown = debounce(searchKeyDown, 300);

  const getAdminUsers = (userId) => {
    let payload = {
      pagination,
      search: userId ? userId : search.length ? search : null,
      applyFilters: applyFilters,
      filters: applyFilters ? filters : null,
    };
    dispatch(fetchAdminUsers(payload));
  };

  const handleViewTransactions = async (selectedUser) => {
    setOpenTransactionsModal(true);
    setSelectedUser(selectedUser);
    const payload = {
      id: selectedUser.id,
      pagination: transactionPagination,
    };
    const response = await dispatch(fetchUserWalletHistory(payload));
    setTransactionsData(response);
  };

  const exportUsers = async () => {
    try {
      setLoading(true);
      const payload = {
        ids: selectedRows[0] === -1 ? -1 : selectedRows,
        action: 'export',
        search: userId ? userId : search.length ? search : null,
        applyFilters: applyFilters,
        filters: applyFilters ? filters : null,
      };
      const response = await userBulkActions(payload);
      getAdminUsers();
      setSelectedRows([]);
      dispatch(success(response.data.message));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message));
    } finally {
      setExportModal(false);
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    setIsMounted(true);
    userId && setSearch(userId);
    userId && setSearchApplied(true);
    userId ? getAdminUsers(userId) : getAdminUsers();
    dispatch(fetchAdminUsersFilters());
    return () => {
      dispatch({ type: CLEAR_ADMIN_USERS_FILTERS });
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isMounted) {
        if (openTransactionsModal) {
          const response = await dispatch(
            fetchUserWalletHistory({
              id: selectedUser.id,
              pagination: transactionPagination,
            })
          );
          setTransactionsData(response); // Set the fetched data into transactionsData
        } else {
          getAdminUsers();
        }
      }
    };

    fetchData();
  }, [pagination.page, transactionPagination.page, pagination.pageSize, filterSize, filters]);

  useEffect(() => {
    if (!search && isMounted) {
      setSearchApplied(false);
      setSearch('');
      getAdminUsers();
    }
  }, [search]);

  const noAdminUsers = () => {
    return (
      <Container
        maxWidth={maxWidth}
        className="olcAdminEmptyPage"
        // sx={{
        //   margin: '120px auto 50px',
        // }}
      >
        <NoAdminUsersScreen />
      </Container>
    );
  };

  if (adminUsers === 0 && adminUsers !== null) {
    return noAdminUsers();
  }

  return (
    <>
      <Container maxWidth="xxl" className="olcAdminListPage">
        <Grid container className="olcAdminListToolbar">
          <Grid item lg={12} md={12} sm={12} xs={12} className="searchBtnWrapper">
            <Box className="searchWrapper">
              <OlcSearchInput
                type="text"
                value={search}
                searchApplied={searchApplied}
                onChange={(e) => {
                  if (e.target.value.length > 9 && isNumberString(e.target.value)) return;
                  e.target.value == ' '
                    ? setSearch(e.target.value.trim())
                    : setSearch(e.target.value);
                }}
                onKeyDown={debouncedHandleKeyDown}
                placeholder={MESSAGES.ADMIN_DASHBOARD.USERS.SEARCH_PLACE_HOLDER}
                inputProps={{ 'aria-label': MESSAGES.ADMIN_DASHBOARD.USERS.SEARCH_PLACE_HOLDER }}
                onClear={() => setSearch('')}
              />
              <Box className="filterBtnWrapper">
                <OlcButton
                  variant="secondary"
                  onClick={() => {
                    setOpenAdvanceFilters(true);
                  }}
                >
                  Filters
                </OlcButton>
                {filterSize > 0 && <Typography className="filtersCounter">{filterSize}</Typography>}
              </Box>
              {filterSize > 0 && (
                <OlcButton
                  variant="secondary"
                  onClick={() => {
                    childRef.current.clearFilters();
                  }}
                  className="clearFilterBtn"
                >
                  {MESSAGES.ADMIN_DASHBOARD.USERS.CLEAR_FILTER_BTN_TEXT}
                </OlcButton>
              )}
            </Box>
            <Box className="adminUserActionsBtnWrapper">
              {selectedRows.length > 0 && (
                <>
                  <OlcButton
                    variant="secondary"
                    onClick={() => {
                      setExportModal(true);
                    }}
                    startIcon={<ExportModalCIcon fill="#000" />}
                  >
                    {MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.EXPORT_BTN_TEXT}
                  </OlcButton>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <Table
          pagination={pagination}
          updatePagination={setPagination}
          setOpenActionModal={setOpenActionModal}
          setSelectedUser={setSelectedUser}
          selectedUser={selectedUser}
          setOpenFundsModal={setOpenFundsModal}
          searchApplied={searchApplied}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          setOpenRemoveFundsModal={setOpenRemoveFundsModal}
          setOpenTransactionsModal={setOpenTransactionsModal}
          handleViewTransactions={handleViewTransactions}
        />
        <AdvanceFilters
          open={openAdvanceFilters}
          handleCloseFilter={() => setOpenAdvanceFilters(false)}
          ref={childRef}
          updatePagination={setPagination}
        />
        <ActionModal
          openModal={openActionModal}
          handleCloseModal={() => setOpenActionModal(false)}
          selectedUser={selectedUser}
          getAdminUsers={getAdminUsers}
        />
        <RemoveFundsByAdmin
          open={openRemoveFundsModal}
          handleClose={() => {
            setOpenRemoveFundsModal(false);
          }}
          selectedUser={selectedUser}
        />
        {openTransactionsModal && (
          <TransactionsModal
            open={openTransactionsModal}
            handleClose={() => {
              setOpenTransactionsModal(false);
              setSelectedUser(false);
              setTransactionsData([]);
              setTransactionPagination(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE);
            }}
            pagination={transactionPagination}
            updatePagination={setTransactionPagination}
            transactionsData={transactionsData}
          />
        )}
        <AddFundsModal
          open={openFundsModal}
          handleClose={() => {
            setOpenFundsModal(false);
          }}
          selectedUser={selectedUser}
        />
        <ExportModal
          open={exportModal}
          handleExport={exportUsers}
          handleClose={() => {
            setExportModal(false);
          }}
          loading={loading}
          header={
            selectedRows[0] === -1 || selectedRows.length > 1
              ? MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.MULTI_EXPORT_HEADER
              : MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.SINGLE_EXPORT_HEADER
          }
          message={
            selectedRows[0] === -1 || selectedRows.length > 1
              ? MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.EXPORT_USERS
              : MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.EXPORT_SINGLE_USERS
          }
          subject={MESSAGES.ADMIN_DASHBOARD.USERS.EXPORT.EXPORT_MESSAGE}
        />
      </Container>
    </>
  );
};

export default AdminUsers;
